// Pure hostname -> route resolver for the multi-hostname EPK app.
//
// ADR 0002 (dev-docs/adr/0002-testimonials-one-app-many-hostnames.md): one
// React app serves epk.highstreetrock.com, testimonials.highstreetrock.com,
// and epk-test.mamafern.cloud (plus local dev) — the frontend decides what
// mounts at `/` (and `/submit`) by inspecting `window.location.hostname`.
// Kept as a pure function + plain data map (no if-chain) so a future
// subdomain is one entry away. No React/DOM here — unit-testable in
// isolation, same spirit as components/audioPlaylist.js.

export const ROOT_EPK = 'epk';
export const ROOT_TESTIMONIALS = 'testimonials';

export const SUBMIT_NONE = null;
export const SUBMIT_TESTIMONIALS = 'testimonials-submit';

const DEFAULT_MOUNT = Object.freeze({ root: ROOT_EPK, submit: SUBMIT_NONE });

// Data-driven hostname -> mount map. Add a new subdomain by adding a row
// here; resolveHostnameRoutes() never needs an if/else added to grow.
const HOSTNAME_MOUNTS = Object.freeze({
  'testimonials.highstreetrock.com': Object.freeze({
    root: ROOT_TESTIMONIALS,
    submit: SUBMIT_TESTIMONIALS,
  }),
});

// Component keys for the path-route table below. These are plain strings,
// not React component references — this module stays React/DOM-free so it
// can be unit-tested in isolation. App.js is the single place that maps a
// key to an actual component.
export const COMPONENT_TESTIMONIALS_PAGE = 'testimonials-page';
export const COMPONENT_SUBMIT_PAGE = 'submit-page';
export const COMPONENT_TESTIMONIALS_EMBED = 'testimonials-embed';

// Path routes that must resolve identically on EVERY hostname, regardless
// of what mounts at `/`. On the testimonials hostname this means both `/`
// and `/testimonials` render the same page (same for `/submit` and
// `/testimonials/submit`) — intentional overlap, not a bug.
//
// This is the single source of truth for those three routes: App.js maps
// over this array to build its <Route> elements instead of hardcoding the
// paths a second time, so adding/renaming a row here is the only edit
// needed (paired with a component-key entry in App.js's registry if it's
// a genuinely new component).
export const PATH_ROUTES = Object.freeze([
  Object.freeze({ path: '/testimonials', component: COMPONENT_TESTIMONIALS_PAGE }),
  Object.freeze({ path: '/testimonials/submit', component: COMPONENT_SUBMIT_PAGE }),
  Object.freeze({ path: '/embed/testimonials', component: COMPONENT_TESTIMONIALS_EMBED }),
]);

/**
 * Normalize a hostname for lookup: trim, lowercase, strip a leading "www.".
 * `WWW.Testimonials.HighStreetRock.com` resolves identically to the bare
 * `testimonials.highstreetrock.com`.
 */
function normalizeHostname(hostname) {
  return String(hostname || '').trim().toLowerCase().replace(/^www\./, '');
}

/**
 * Resolve which component set mounts at `/` and `/submit` for a given
 * hostname. Returns `{ root, submit }` where `root` is one of ROOT_EPK /
 * ROOT_TESTIMONIALS and `submit` is SUBMIT_NONE or SUBMIT_TESTIMONIALS.
 * Unknown, missing, or unmapped hostnames (localhost, the main EPK domain,
 * a typo, SSR with no hostname yet) fall back to the default EPK app —
 * never a hard error.
 */
export function resolveHostnameRoutes(hostname) {
  const key = normalizeHostname(hostname);
  return HOSTNAME_MOUNTS[key] || DEFAULT_MOUNT;
}

export const EPK_ABSOLUTE_URL = 'https://epk.highstreetrock.com/';

/**
 * Href that reaches the EPK from any hostname this build serves. On hosts
 * where the EPK mounts at `/` (the prod EPK domain, epk-test staging, local
 * dev) a relative `/` keeps navigation on the current origin; on hostnames
 * that mount something else at `/` (testimonials.highstreetrock.com) only
 * the absolute EPK URL can get there.
 */
export function epkHref(hostname) {
  return resolveHostnameRoutes(hostname).root === ROOT_EPK ? '/' : EPK_ABSOLUTE_URL;
}
